import React from "react";
import { createPortal } from "react-dom";
import { Tooltip } from "./Tooltip";
import { MdDelete } from "react-icons/md";
import type { Bar, TimeSignature } from "./SongStructureEditor";
import { useToast } from "../hooks/useToast";
import { PromptModal } from "./PromptModal";

type DenominatorMenuRect = { top: number; left: number; width: number } | null;

type SongStructureBarRowProps = {
  bar: Bar;
  index: number;
  bars: Bar[];
  validDenominators: number[];
  updateBar: (id: string, patch: Partial<Bar>) => void;
  updateTimeSig: (id: string, patch: Partial<TimeSignature>) => void;
  removeBar: (id: string) => void;
  pendingEndValues: Record<string, number | undefined>;
  setPendingEndValues: React.Dispatch<
    React.SetStateAction<Record<string, number | undefined>>
  >;
  flashStartIds: string[];
  flashEndIds: string[];
  setBars: React.Dispatch<React.SetStateAction<Bar[]>>;
  setFlashStartIds: React.Dispatch<React.SetStateAction<string[]>>;
  setFlashEndIds: React.Dispatch<React.SetStateAction<string[]>>;
  openDenominatorFor: string | null;
  setOpenDenominatorFor: React.Dispatch<React.SetStateAction<string | null>>;
  denominatorMenuRect: DenominatorMenuRect;
  setDenominatorMenuRect: React.Dispatch<
    React.SetStateAction<DenominatorMenuRect>
  >;
};

export const SongStructureBarRow: React.FC<SongStructureBarRowProps> = ({
  bar,
  index,
  bars,
  validDenominators,
  updateBar,
  updateTimeSig,
  removeBar,
  pendingEndValues,
  setPendingEndValues,
  flashStartIds,
  flashEndIds,
  setBars,
  setFlashStartIds,
  setFlashEndIds,
  openDenominatorFor,
  setOpenDenominatorFor,
  denominatorMenuRect,
  setDenominatorMenuRect,
}) => {
  const { showError } = useToast();
  const DeleteIcon = MdDelete as React.ComponentType<any>;
  const endInputRef = React.useRef<HTMLInputElement | null>(null);
  const [confirmModalOpen, setConfirmModalOpen] = React.useState(false);
  const [pendingConfirmData, setPendingConfirmData] = React.useState<{
    clampedEnd: number;
    previousEnd: number;
  } | null>(null);
  return (
    <div
      className="bar-row"
      style={{
        display: "grid",
        gridTemplateColumns: "1.1fr 1fr 1fr max-content",
        alignItems: "center",
        gap: "16px",
        padding: "14px 14px",
        background: "rgba(255,255,255,0.04)",
        borderRadius: "12px",
        marginBottom: "12px",
      }}
    >
      <div
        className="bar-section"
        style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          opacity: 0.9,
        }}
      >
        <label
          className="section-header"
          style={{ fontSize: "13px", opacity: 0.8, width: 40 }}
        >
          Bar(s)
        </label>
        <div
          className="input-row"
          style={{ display: "flex", alignItems: "center", gap: "6px" }}
        >
          <input
            type="number"
            value={index === 0 ? 1 : bars[index - 1].endBar + 1}
            disabled
            readOnly
            className={flashStartIds.includes(bar.id) ? "flash" : undefined}
            style={{
              width: "64px",
              padding: "10px 12px",
              borderRadius: "10px",
              border: "1px solid rgba(255,255,255,0.1)",
              background: "rgba(0,0,0,0.15)",
              color: "white",
              textAlign: "center",
              opacity: 0.8,
            }}
          />
          <span style={{ opacity: 0.6 }}>-</span>
          <input
            type="number"
            inputMode="numeric"
            min={bar.startBar}
            max={999}
            value={
              Object.prototype.hasOwnProperty.call(pendingEndValues, bar.id)
                ? (pendingEndValues[bar.id] as number | undefined) ?? ""
                : bar.endBar
            }
            ref={endInputRef}
            onFocus={(e) => {
              e.currentTarget.select();
            }}
            onChange={(e) => {
              const raw = Number(e.target.value);
              setPendingEndValues((prev) => ({
                ...prev,
                [bar.id]: raw,
              }));
            }}
            onBlur={(e) => {
              const raw = Object.prototype.hasOwnProperty.call(
                pendingEndValues,
                bar.id
              )
                ? (pendingEndValues[bar.id] as number | undefined)
                : Number(e.currentTarget.value);
              const clampedEnd = Number.isFinite(raw)
                ? Math.min(
                    999,
                    Math.max(bar.startBar, Math.floor(raw as number))
                  )
                : bar.startBar;
              const previousEnd = bar.endBar;

              const originalLengths = bars.map((b) =>
                Math.max(1, (b.endBar ?? b.startBar) - b.startBar + 1)
              );
              const next = bars.map((b) => ({ ...b }));
              next[index] = { ...next[index], endBar: clampedEnd };
              let overflowIndex: number | null = null;
              for (let i = index + 1; i < next.length; i++) {
                const newStart = next[i - 1].endBar + 1;
                const newEnd = newStart + originalLengths[i] - 1;
                if (newStart > 999 || newEnd > 999) {
                  overflowIndex = i;
                  break;
                }
                next[i] = {
                  ...next[i],
                  startBar: newStart,
                  endBar: newEnd,
                };
              }

              if (overflowIndex !== null) {
                setPendingConfirmData({ clampedEnd, previousEnd });
                setConfirmModalOpen(true);
                return;
              } else {
                setBars(next);
                const startIds = next.slice(index + 1).map((b) => b.id);
                const endIds = [next[index].id, ...startIds];
                setFlashEndIds(endIds);
                setFlashStartIds(startIds);
                setTimeout(() => {
                  setFlashEndIds([]);
                  setFlashStartIds([]);
                }, 520);
                e.currentTarget.value = String(clampedEnd);
              }

              setPendingEndValues((prevMap) => {
                const nm = { ...prevMap };
                delete nm[bar.id];
                return nm;
              });
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                const input = e.currentTarget as HTMLInputElement;
                const raw = Object.prototype.hasOwnProperty.call(
                  pendingEndValues,
                  bar.id
                )
                  ? (pendingEndValues[bar.id] as number | undefined)
                  : Number(input.value);
                const clampedEnd = Number.isFinite(raw)
                  ? Math.min(
                      999,
                      Math.max(bar.startBar, Math.floor(raw as number))
                    )
                  : bar.startBar;
                const previousEnd = bar.endBar;
                const originalLengths = bars.map((b) =>
                  Math.max(1, (b.endBar ?? b.startBar) - b.startBar + 1)
                );
                const next = bars.map((b) => ({ ...b }));
                next[index] = { ...next[index], endBar: clampedEnd };
                let overflowIndex: number | null = null;
                for (let i = index + 1; i < next.length; i++) {
                  const newStart = next[i - 1].endBar + 1;
                  const newEnd = newStart + originalLengths[i] - 1;
                  if (newStart > 999 || newEnd > 999) {
                    overflowIndex = i;
                    break;
                  }
                  next[i] = {
                    ...next[i],
                    startBar: newStart,
                    endBar: newEnd,
                  };
                }

                if (overflowIndex !== null) {
                  setPendingConfirmData({ clampedEnd, previousEnd });
                  setConfirmModalOpen(true);
                  return;
                } else {
                  setBars(next);
                  const startIds = next.slice(index + 1).map((b) => b.id);
                  const endIds = [next[index].id, ...startIds];
                  setFlashEndIds(endIds);
                  setFlashStartIds(startIds);
                  setTimeout(() => {
                    setFlashEndIds([]);
                    setFlashStartIds([]);
                  }, 520);
                  input.value = String(clampedEnd);
                  input.blur();
                }
                setPendingEndValues((prev) => {
                  const next = { ...prev };
                  delete next[bar.id];
                  return next;
                });
              }
            }}
            className={flashEndIds.includes(bar.id) ? "flash" : undefined}
            style={{
              width: "68px",
              padding: "10px 12px",
              borderRadius: "10px",
              border: "1px solid rgba(255,255,255,0.1)",
              background: "rgba(0,0,0,0.15)",
              color: "white",
              textAlign: "center",
            }}
          />
        </div>
      </div>

      <div
        className="bar-section"
        style={{ display: "flex", alignItems: "center", gap: "6px" }}
      >
        <label
          className="section-header"
          style={{ fontSize: "13px", opacity: 0.8, width: 40 }}
        >
          BPM
        </label>
        <div
          className="input-row"
          style={{ display: "flex", alignItems: "center", gap: "6px" }}
        >
          <input
            type="number"
            inputMode="numeric"
            min={30}
            max={300}
            value={bar.bpm}
            onFocus={(e) => {
              e.currentTarget.select();
            }}
            onChange={(e) => {
              const raw = Number(e.target.value);
              updateBar(bar.id, { bpm: raw });
            }}
            onBlur={(e) => {
              const raw = Number(e.currentTarget.value);
              const clamped = Number.isFinite(raw)
                ? Math.max(30, Math.min(300, raw))
                : 30;
              if (clamped !== bar.bpm) {
                updateBar(bar.id, { bpm: clamped });
              }
              if (Number.isFinite(raw) && clamped !== raw) {
                showError("Enter a BPM in the range of 30 to 300");
              }
              e.currentTarget.value = String(clamped);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                const input = e.currentTarget as HTMLInputElement;
                const raw = Number(input.value);
                const clamped = Number.isFinite(raw)
                  ? Math.max(30, Math.min(300, raw))
                  : 30;
                if (clamped !== bar.bpm) {
                  updateBar(bar.id, { bpm: clamped });
                }
                if (Number.isFinite(raw) && clamped !== raw) {
                  showError("Enter a BPM in the range of 30 to 300");
                }
                input.value = String(clamped);
                input.blur();
              }
            }}
            style={{
              width: "78px",
              padding: "10px 12px",
              borderRadius: "10px",
              border: "1px solid rgba(255,255,255,0.1)",
              background: "rgba(0,0,0,0.15)",
              color: "white",
              textAlign: "center",
            }}
          />
        </div>
      </div>

      <div
        className="bar-section"
        style={{ display: "flex", alignItems: "center", gap: "6px" }}
      >
        <label
          className="section-header"
          style={{ fontSize: "13px", opacity: 0.8, width: 112 }}
        >
          Time Signature
        </label>
        <div
          className="input-row"
          style={{ display: "flex", alignItems: "center", gap: "6px" }}
        >
          <input
            type="number"
            inputMode="numeric"
            min={2}
            max={16}
            value={bar.timeSig.numerator}
            onFocus={(e) => {
              e.currentTarget.select();
            }}
            onChange={(e) =>
              updateTimeSig(bar.id, {
                numerator: Number(e.target.value),
              })
            }
            onBlur={(e) => {
              const raw = Number(e.currentTarget.value);
              const clamped = Number.isFinite(raw)
                ? Math.max(2, Math.min(16, Math.floor(raw)))
                : 2;
              if (clamped !== bar.timeSig.numerator) {
                updateTimeSig(bar.id, { numerator: clamped });
              }
              if (Number.isFinite(raw) && clamped !== raw) {
                showError("Enter a numerator in the range of 2 to 16");
              }
              e.currentTarget.value = String(clamped);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                const input = e.currentTarget as HTMLInputElement;
                const raw = Number(input.value);
                const clamped = Number.isFinite(raw)
                  ? Math.max(2, Math.min(16, Math.floor(raw)))
                  : 2;
                if (clamped !== bar.timeSig.numerator) {
                  updateTimeSig(bar.id, { numerator: clamped });
                }
                if (Number.isFinite(raw) && clamped !== raw) {
                  showError("Enter a numerator in the range of 2 to 16");
                }
                input.value = String(clamped);
                input.blur();
              }
            }}
            className="time-sig-input"
            style={{
              width: "40px",
              height: "40px",
              boxSizing: "border-box",
              padding: "8px",
              borderRadius: "10px",
              border: "1px solid rgba(255,255,255,0.1)",
              background: "rgba(0,0,0,0.15)",
              color: "white",
              textAlign: "center",
            }}
          />
          <span style={{ opacity: 0.6 }}>/</span>
          <div
            className="time-sig-denom-container"
            style={{ position: "relative", display: "inline-block" }}
          >
            <button
              type="button"
              aria-haspopup="listbox"
              aria-expanded={openDenominatorFor === bar.id}
              onClick={(e) => {
                const btn = e.currentTarget as HTMLButtonElement;
                const rect = btn.getBoundingClientRect();
                setDenominatorMenuRect({
                  top: rect.bottom + 4,
                  left: rect.left,
                  width: Math.max(70, rect.width),
                });
                setOpenDenominatorFor((prev) =>
                  prev === bar.id ? null : bar.id
                );
              }}
              className="time-sig-input"
              style={{
                width: "40px",
                height: "40px",
                boxSizing: "border-box",
                padding: "8px",
                borderRadius: "10px",
                border: "1px solid rgba(255,255,255,0.1)",
                background: "rgba(0,0,0,0.15)",
                color: "white",
                textAlign: "center",
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "var(--button-hover-bg)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "rgba(0,0,0,0.15)";
              }}
            >
              {bar.timeSig.denominator}
            </button>
            {openDenominatorFor === bar.id &&
              denominatorMenuRect &&
              createPortal(
                <div
                  className="time-sig-denom-menu"
                  role="listbox"
                  style={{
                    position: "fixed",
                    top: denominatorMenuRect.top,
                    left: denominatorMenuRect.left,
                    width: denominatorMenuRect.width,
                    background: "var(--dropdown-bg)",
                    border: "none",
                    borderRadius: "12px",
                    backdropFilter: "blur(10px)",
                    boxShadow: "0 6px 16px rgba(0, 0, 0, 0.3)",
                    zIndex: 100000,
                    overflow: "hidden",
                  }}
                >
                  {validDenominators.map((d) => (
                    <button
                      key={d}
                      role="option"
                      aria-selected={d === bar.timeSig.denominator}
                      onClick={() => {
                        updateTimeSig(bar.id, { denominator: d });
                        setOpenDenominatorFor(null);
                        setDenominatorMenuRect(null);
                      }}
                      style={{
                        width: "100%",
                        padding: "8px 10px",
                        fontSize: "15px",
                        background:
                          d === bar.timeSig.denominator
                            ? "var(--button-hover-bg)"
                            : "transparent",
                        color: "var(--text-primary)",
                        border: "none",
                        cursor: "pointer",
                        fontWeight: 500,
                        textAlign: "center",
                        transition: "all 0.2s ease",
                      }}
                      onMouseEnter={(e) => {
                        if (d !== bar.timeSig.denominator) {
                          e.currentTarget.style.background = "var(--button-bg)";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (d !== bar.timeSig.denominator) {
                          e.currentTarget.style.background = "transparent";
                        }
                      }}
                    >
                      {d}
                    </button>
                  ))}
                </div>,
                document.body
              )}
          </div>
        </div>
      </div>

      {bars.length > 1 && (
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Tooltip text="Remove bar" position="bottom">
            <button
              className="delete-button"
              aria-label="Remove bar"
              onClick={() => removeBar(bar.id)}
              style={{
                width: "40px",
                height: "40px",
                borderRadius: "12px",
                border: "none",
                background: "transparent",
                color: "rgba(221, 135, 135, 0.65)",
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                transition:
                  "color 0.2s ease, background 0.2s ease, border-color 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = "rgba(222, 79, 79, 0.9)";
                e.currentTarget.style.background = "rgba(220, 38, 38, 0.15)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = "rgba(221, 135, 135, 0.65)";
                e.currentTarget.style.background = "transparent";
              }}
            >
              <DeleteIcon style={{ fontSize: "20px" }} />
            </button>
          </Tooltip>
        </div>
      )}

      <PromptModal
        isOpen={confirmModalOpen}
        title="Truncate bars?"
        message={
          "The maximum number of bars in a song is 999. All subsequent bars will be deleted. Proceed?"
        }
        confirmText="Yes"
        cancelText="Cancel"
        onCancel={() => {
          if (pendingConfirmData) {
            const { previousEnd } = pendingConfirmData;
            if (endInputRef.current) {
              endInputRef.current.value = String(previousEnd);
              endInputRef.current.blur();
            }
            setPendingEndValues((prevMap) => {
              const nm = { ...prevMap };
              delete nm[bar.id];
              return nm;
            });
          }
          setConfirmModalOpen(false);
          setPendingConfirmData(null);
        }}
        onConfirm={() => {
          if (!pendingConfirmData) return;
          const { clampedEnd } = pendingConfirmData;
          const originalLengths = bars.map((b) =>
            Math.max(1, (b.endBar ?? b.startBar) - b.startBar + 1)
          );
          const applied = bars.map((b) => ({ ...b }));
          applied[index] = {
            ...applied[index],
            endBar: clampedEnd,
          };
          let overflowIndex: number | null = null;
          for (let i = index + 1; i < applied.length; i++) {
            const newStart = applied[i - 1].endBar + 1;
            const newEnd = newStart + originalLengths[i] - 1;
            if (newStart > 999 || newEnd > 999) {
              overflowIndex = i;
              break;
            }
            applied[i] = {
              ...applied[i],
              startBar: newStart,
              endBar: newEnd,
            };
          }
          if (overflowIndex !== null) {
            const truncated = applied.slice(0, overflowIndex);
            setBars(truncated);
            const startIds = truncated.slice(index + 1).map((b) => b.id);
            const endIds = [truncated[index].id, ...startIds];
            setFlashEndIds(endIds);
            setFlashStartIds(startIds);
            setTimeout(() => {
              setFlashEndIds([]);
              setFlashStartIds([]);
            }, 520);
          } else {
            // Fallback: no overflow, apply normally
            setBars(applied);
            const startIds = applied.slice(index + 1).map((b) => b.id);
            const endIds = [applied[index].id, ...startIds];
            setFlashEndIds(endIds);
            setFlashStartIds(startIds);
            setTimeout(() => {
              setFlashEndIds([]);
              setFlashStartIds([]);
            }, 520);
          }
          if (endInputRef.current) {
            endInputRef.current.value = String(clampedEnd);
            endInputRef.current.blur();
          }
          setPendingEndValues((prevMap) => {
            const nm = { ...prevMap };
            delete nm[bar.id];
            return nm;
          });
          setConfirmModalOpen(false);
          setPendingConfirmData(null);
        }}
      />
    </div>
  );
};
